import { useState, useEffect } from 'react';
import { Search, UserPlus, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import { supabase } from '@/lib/supabase';
import { captureError } from '@/lib/sentry';

interface SharedMember {
  user_id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  permissions: {
    view: boolean;
    edit: boolean;
    delete: boolean;
    export: boolean;
  };
}

interface Member {
  user_id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
}

interface ShareContactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contactId: string;
  contactName: string;
}

export function ShareContactDialog({
  open,
  onOpenChange,
  contactId,
  contactName,
}: ShareContactDialogProps) {
  const [members, setMembers] = useState<Member[]>([]);
  const [sharedMembers, setSharedMembers] = useState<SharedMember[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const { currentWorkspace } = useWorkspaces();
  const { toast } = useToast();

  useEffect(() => {
    if (open && currentWorkspace) {
      fetchWorkspaceMembers();
      fetchSharedMembers();
    }
  }, [open, currentWorkspace?.id, contactId]);

  const fetchWorkspaceMembers = async () => {
    try {
      const { data, error } = await supabase.rpc('get_workspace_members', {
        p_workspace_id: currentWorkspace?.id
      });

      if (error) throw error;
      setMembers(data);
    } catch (error) {
      console.error('Error fetching members:', error);
      captureError(error as Error, { feature: 'contacts-share-contact-dialog-fetch-workspace-members' });
      toast({
        title: "Error",
        description: "Failed to load workspace members",
        variant: "destructive",
      });
    }
  };

  const fetchSharedMembers = async () => {
    try {
      // Get share records with JSONB permissions
      const { data, error } = await supabase
        .from('contact_shares')
        .select('shared_with_user_id, permissions')
        .eq('contact_id', contactId);

      if (error) throw error;

      // Get workspace members to look up names/emails
      const { data: memberData } = await supabase.rpc('get_workspace_members', {
        p_workspace_id: currentWorkspace?.id
      });

      const memberMap = new Map(memberData?.map((m: Member) => [m.user_id, m]) || []);

      const sharedUsers = data.map(share => {
        const member = memberMap.get(share.shared_with_user_id) as Member | undefined;
        const perms = share.permissions as { view?: boolean; edit?: boolean; delete?: boolean; export?: boolean } || {};
        return {
          user_id: share.shared_with_user_id,
          email: member?.email || '',
          first_name: member?.first_name || null,
          last_name: member?.last_name || null,
          permissions: {
            view: perms.view ?? true,
            edit: perms.edit ?? false,
            delete: perms.delete ?? false,
            export: perms.export ?? false
          }
        };
      });

      setSharedMembers(sharedUsers);
    } catch (error) {
      console.error('Error fetching shared members:', error);
    } finally {
      setLoading(false);
    }
  };

  const shareWithMember = async (member: Member) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase
        .from('contact_shares')
        .insert({
          contact_id: contactId,
          shared_with_user_id: member.user_id,
          workspace_id: currentWorkspace?.id,
          permissions: {
            view: true,
            edit: false,
            delete: false,
            export: false
          },
          created_by: user?.id
        });

      if (error) throw error;

      toast({
        description: `Contact shared with ${member.email}`,
      });

      await fetchSharedMembers();
    } catch (error) {
      console.error('Error sharing contact:', error);
      captureError(error as Error, { feature: 'contacts-share-contact-dialog-share-with-member' });
      toast({
        title: "Error",
        description: "Failed to share contact",
        variant: "destructive",
      });
    }
  };

  const updatePermissions = async (userId: string, permission: string, value: boolean) => {
    try {
      const member = sharedMembers.find(m => m.user_id === userId);
      if (!member) return;

      const updatedPermissions = {
        ...member.permissions,
        [permission]: value
      };

      const { error } = await supabase
        .from('contact_shares')
        .update({ permissions: updatedPermissions })
        .eq('contact_id', contactId)
        .eq('shared_with_user_id', userId);

      if (error) throw error;

      toast({
        description: "Permissions updated successfully",
      });

      await fetchSharedMembers();
    } catch (error) {
      console.error('Error updating permissions:', error);
      captureError(error as Error, { feature: 'contacts-share-contact-dialog-update-permissions' });
      toast({
        title: "Error",
        description: "Failed to update permissions",
        variant: "destructive",
      });
    }
  };

  const removeShare = async (userId: string) => {
    try {
      const { error } = await supabase
        .from('contact_shares')
        .delete()
        .eq('contact_id', contactId)
        .eq('shared_with_user_id', userId);

      if (error) throw error;

      toast({
        description: "Share removed successfully",
      });

      await fetchSharedMembers();
    } catch (error) {
      console.error('Error removing share:', error);
      captureError(error as Error, { feature: 'contacts-share-contact-dialog-remove-share' });
      toast({
        title: "Error",
        description: "Failed to remove share",
        variant: "destructive",
      });
    }
  };

  // Filter members based on search term
  const filteredMembers = members.filter(member => 
    !sharedMembers.some(shared => shared.user_id === member.user_id) &&
    (member.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
     `${member.first_name} ${member.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Share Contact: {contactName}</DialogTitle>
          <DialogDescription>
            Share this contact with other members of your workspace
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search and Add Members */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Add Members</label>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search members..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>
            {searchTerm && filteredMembers.length > 0 && (
              <div className="border rounded-md divide-y">
                {filteredMembers.map((member) => (
                  <div
                    key={member.user_id}
                    className="flex items-center justify-between p-2 hover:bg-accent/50"
                  >
                    <div>
                      <div className="font-medium">
                        {member.first_name} {member.last_name}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {member.email}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => shareWithMember(member)}
                    >
                      <UserPlus className="h-4 w-4 mr-2" />
                      Share
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Shared Members List */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Shared With</label>
            {loading ? (
              <div className="text-center py-4">Loading shared members...</div>
            ) : sharedMembers.length > 0 ? (
              <div className="space-y-2">
                {sharedMembers.map((member) => (
                  <div
                    key={member.user_id}
                    className="flex items-center justify-between p-2 border rounded-md"
                  >
                    <div>
                      <div className="font-medium">
                        {member.first_name} {member.last_name}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {member.email}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex items-center gap-2">
                          <label className="text-sm">View</label>
                          <Switch
                            checked={member.permissions.view}
                            onCheckedChange={(checked) => 
                              updatePermissions(member.user_id, 'view', checked)
                            }
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <label className="text-sm">Edit</label>
                          <Switch
                            checked={member.permissions.edit}
                            onCheckedChange={(checked) => 
                              updatePermissions(member.user_id, 'edit', checked)
                            }
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <label className="text-sm">Delete</label>
                          <Switch
                            checked={member.permissions.delete}
                            onCheckedChange={(checked) => 
                              updatePermissions(member.user_id, 'delete', checked)
                            }
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <label className="text-sm">Export</label>
                          <Switch
                            checked={member.permissions.export}
                            onCheckedChange={(checked) => 
                              updatePermissions(member.user_id, 'export', checked)
                            }
                          />
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeShare(member.user_id)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-muted-foreground">
                This contact hasn't been shared with anyone yet
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}